/**
 * Botanical Index - Product Card Hover Tooltips
 * Displays product cards with image and title when hovering over product links (desktop only)
 */

(function () {
  "use strict";

  // Check if we're on a touch device
  const isTouchDevice = () => {
    return "ontouchstart" in window || navigator.maxTouchPoints > 0;
  };

  // Don't initialize on touch devices
  if (isTouchDevice()) {
    return;
  }

  // Create tooltip element with product card structure
  const tooltip = document.createElement("div");
  tooltip.className = "product-card-tooltip";
  tooltip.setAttribute("aria-hidden", "true");
  tooltip.style.display = "none";
  document.body.appendChild(tooltip);

  const tooltipImageWrapper = document.createElement("div");
  tooltipImageWrapper.className = "product-card-tooltip__image-wrapper";
  tooltip.appendChild(tooltipImageWrapper);

  const tooltipImage = document.createElement("img");
  tooltipImage.className = "product-card-tooltip__image";
  tooltipImage.alt = "";
  tooltipImage.loading = "lazy";
  tooltipImageWrapper.appendChild(tooltipImage);

  const tooltipContent = document.createElement("div");
  tooltipContent.className = "product-card-tooltip__content";
  tooltip.appendChild(tooltipContent);

  const tooltipTitle = document.createElement("div");
  tooltipTitle.className = "product-card-tooltip__title";
  tooltipContent.appendChild(tooltipTitle);

  // Track current hovered link and fetch controller
  let currentLink = null;
  let hideTimeout = null;
  let abortController = null;

  /**
   * Fetch product data from Shopify API
   */
  const fetchProductData = async (productHandle, signal) => {
    try {
      const response = await fetch(`/products/${productHandle}.js`, { signal });
      if (!response.ok) return null;
      const product = await response.json();
      return product;
    } catch (error) {
      // Ignore abort errors (expected when user moves to another product)
      if (error.name === "AbortError") {
        return null;
      }
      console.error("Error fetching product:", error);
      return null;
    }
  };

  /**
   * Position tooltip near the cursor
   */
  const positionTooltip = (event) => {
    const offset = 20; // Distance from cursor
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = event.clientX + offset;
    let y = event.clientY + offset;

    // Adjust if tooltip would overflow viewport
    if (x + tooltipRect.width > viewportWidth) {
      x = event.clientX - tooltipRect.width - offset;
    }

    if (y + tooltipRect.height > viewportHeight) {
      y = event.clientY - tooltipRect.height - offset;
    }

    // Ensure tooltip doesn't go off-screen
    x = Math.max(10, Math.min(x, viewportWidth - tooltipRect.width - 10));
    y = Math.max(10, Math.min(y, viewportHeight - tooltipRect.height - 10));

    tooltip.style.left = x + "px";
    tooltip.style.top = y + "px";
  };

  // Prefix-proof: matches under market/locale prefixes like /en-ca/collections/opus-one
  const isOpusCollectionHref = (href) => {
    try {
      return /\/collections\/opus-/.test(new URL(href, window.location.origin).pathname);
    } catch (error) {
      return false;
    }
  };

  /**
   * Show tooltip with product card
   */
  const showTooltip = async (link, event) => {
    const href = link.getAttribute("href") || "";

    // Opus links point at collections; Liquid supplies the Hand Soap image and
    // title for them, so they never fetch product data.
    const isOpusCollection = isOpusCollectionHref(href);
    if (!href.startsWith("/products/") && !isOpusCollection) {
      return;
    }

    let imageUrl = link.getAttribute("data-product-image");
    let productTitle = link.getAttribute("data-product-title");
    const productHandle = href.replace("/products/", "");

    if (productHandle.startsWith("opus-")) {
      return;
    }

    if (isOpusCollection && (!imageUrl || !productTitle)) {
      return;
    }

    // Cancel any pending fetch from previous hover
    if (abortController) {
      abortController.abort();
    }

    // Create new abort controller for this hover
    abortController = new AbortController();
    const currentSignal = abortController.signal;

    // Clear any pending hide
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }

    // Update current link immediately
    currentLink = link;

    // If image URL is missing from Liquid, fetch from Ajax API
    if (!imageUrl || imageUrl === "") {
      console.log(`[Botanical Index] Fetching missing data for: ${productHandle}`);

      // Show loading state
      tooltipTitle.textContent = productTitle || "Loading...";
      tooltip.style.display = "block";
      positionTooltip(event);

      requestAnimationFrame(() => {
        tooltip.classList.add("visible");
      });

      // Fetch complete product data from Ajax API
      const productData = await fetchProductData(productHandle, currentSignal);

      // Check if user still hovering the same link (and fetch wasn't cancelled)
      if (productData && currentLink === link && !currentSignal.aborted) {
        imageUrl = productData.featured_image;
        productTitle = productData.title;

        // Update data attributes for future hovers (cache)
        link.setAttribute("data-product-image", imageUrl);
        link.setAttribute("data-product-title", productTitle);

        // Update tooltip with fetched data
        tooltipImage.src = imageUrl;
        tooltipTitle.textContent = productTitle;
      } else if (!productData && !currentSignal.aborted) {
        // Product not found via Ajax API (only hide if not aborted)
        hideTooltip();
      }

      return;
    }

    // Standard flow: use Liquid-provided data
    // Only show if this is still the current link (user hasn't moved away)
    if (currentLink === link) {
      tooltipImage.src = imageUrl;
      tooltipTitle.textContent = productTitle || "";

      // Show tooltip
      tooltip.style.display = "block";
      positionTooltip(event);

      // Add visible class after a brief delay for smooth transition
      requestAnimationFrame(() => {
        tooltip.classList.add("visible");
      });
    }
  };

  /**
   * Hide tooltip
   */
  const hideTooltip = () => {
    // Cancel any pending fetch
    if (abortController) {
      abortController.abort();
      abortController = null;
    }

    tooltip.classList.remove("visible");

    // Hide after transition completes
    hideTimeout = setTimeout(() => {
      tooltip.style.display = "none";
      currentLink = null;
    }, 200); // Match CSS transition duration
  };

  /**
   * Handle mouse enter on product link
   */
  const handleMouseEnter = (event) => {
    const link = event.currentTarget;
    showTooltip(link, event);
  };

  /**
   * Handle mouse move on product link
   */
  const handleMouseMove = (event) => {
    if (currentLink && tooltip.classList.contains("visible")) {
      positionTooltip(event);
    }
  };

  /**
   * Handle mouse leave on product link
   */
  const handleMouseLeave = () => {
    hideTooltip();
  };

  /**
   * Initialize hover listeners on all product links
   */
  const initializeProductLinks = () => {
    const productLinks = document.querySelectorAll(".botanical-product-link");

    productLinks.forEach((link) => {
      const href = link.getAttribute("href") || "";

      // Only attach tooltip listeners for product URLs and Opus collection links
      // Note: Product links don't need data-product-image, we can fetch it via Ajax API
      if (!href.startsWith("/products/") && !isOpusCollectionHref(href)) return;

      link.addEventListener("mouseenter", handleMouseEnter);
      link.addEventListener("mousemove", handleMouseMove);
      link.addEventListener("mouseleave", handleMouseLeave);
    });
  };

  /**
   * Cleanup function
   */
  const cleanup = () => {
    const productLinks = document.querySelectorAll(".botanical-product-link");

    productLinks.forEach((link) => {
      link.removeEventListener("mouseenter", handleMouseEnter);
      link.removeEventListener("mousemove", handleMouseMove);
      link.removeEventListener("mouseleave", handleMouseLeave);
    });

    if (tooltip && tooltip.parentNode) {
      tooltip.parentNode.removeChild(tooltip);
    }

    if (hideTimeout) {
      clearTimeout(hideTimeout);
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeProductLinks);
  } else {
    initializeProductLinks();
  }

  // Cleanup on page unload
  window.addEventListener("beforeunload", cleanup);

  // WCAG 1.4.13: hovered content must be dismissible without moving the pointer
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && tooltip.classList.contains("visible")) {
      hideTooltip();
    }
  });

  // Hide tooltip when scrolling
  window.addEventListener(
    "scroll",
    () => {
      if (currentLink) {
        hideTooltip();
      }
    },
    { passive: true },
  );

  // Hide tooltip when window is resized
  window.addEventListener(
    "resize",
    () => {
      if (currentLink) {
        hideTooltip();
      }
    },
    { passive: true },
  );
})();
